import {SurveyPreset} from 'client/common/types/survey';
import {Features} from 'client/common/types';

const DEFAULT_COPY_CONFIG = {
    access: {enabled: true},
    metrika: {enabled: true},
    redirects: {enabled: true},
    audience: {enabled: true}
};

export const PRO_SURVEY_FEATURES: Features = {
    advancedLogic: true,
    advancedQuestionLabelFormat: true,
    canChangeQuestionLabelAttachmentPosition: true,
    canRotateQuestions: true,
    canEditBranding: true,
    canEditTheme: true,
    brandingEdit: {
        logo: {enabled: true},
        colors: {enabled: true},
        bg: {enabled: true},
        underlay: {enabled: true},
        fonts: {enabled: true},
        footer: {enabled: true},
        progress: {enabled: true}
    },
    pagesMode: true,
    questionLabelAttachments: {
        image: true,
        video: true
    },
    copy: DEFAULT_COPY_CONFIG,
    requiredSetting: true,
    choice: {
        enabled: true,
        advancedOptionLabelFormat: true,
        canUseOptionAttachment: true,
        noAnswer: true,
        noOpinionAnswer: true,
        optionLogic: true,
        otherAnswer: true,
        otherAnswerWithText: true,
        restriction: true
    },
    date: {
        enabled: true
    },
    email: {
        enabled: true
    },
    emotion: {
        enabled: true,
        noOpinionAnswer: true,
        labelsEnabled: true,
        maxLabelLength: 18,
        otherAnswer: true,
        otherAnswerWithText: true
    },
    geoText: {
        enabled: true
    },
    longText: {
        enabled: true
    },
    number: {
        enabled: true,
        restriction: true
    },
    rating: {
        enabled: true,
        otherAnswer: true,
        otherAnswerWithText: true,
        noOpinionAnswer: true
    },
    scale: {
        enabled: true,
        noOpinionAnswer: true,
        otherAnswer: true,
        otherAnswerWithText: true,
        canChangeLabelsPosition: true,
        limitsCustomization: {
            min: true,
            max: true
        },
        showMiddleLabel: true
    },
    shortText: {
        enabled: true
    },
    sidebyside: {
        enabled: true,
        noOpinionAnswer: true,
        noAnswer: true
    },
    table: {
        enabled: true,
        canUseOptionAttachment: true,
        advancedRowLabelFormat: true,
        advancedColumnLabelFormat: true,
        maxColumnLabelLength: 30,
        maxRowLabelLength: 130,
        maxColumnImages: 1,
        maxRowImages: 1
    },
    tableScale: {
        enabled: true,
        canUseOptionAttachment: true,
        advancedRowLabelFormat: true,
        noOpinionAnswer: true,
        maxColumnLabelLength: 30,
        maxRowLabelLength: 130,
        maxRowImages: 1,
        limitsCustomization: {
            min: true,
            max: true
        }
    },
    markup: {
        enabled: true
    },
    canAddQuestions: true,
    canRemoveQuestions: true,
    canMoveQuestions: true,
    canChangeLogic: true,
    canChangeQuestionType: true
};

export const UX_SURVEY_FEATURES: Features = {
    advancedLogic: true,
    advancedQuestionLabelFormat: true,
    canChangeQuestionLabelAttachmentPosition: false,
    pagesMode: false,
    requiredSetting: true,
    maxCustomQuestions: 10,
    copy: {
        access: {enabled: true}
    },
    choice: {
        enabled: true,
        advancedOptionLabelFormat: true,
        canUseOptionAttachment: false,
        noAnswer: true,
        noOpinionAnswer: true,
        optionCountLimit: 6,
        optionLogic: true,
        otherAnswer: true,
        otherAnswerWithText: true,
        restriction: true
    },
    date: {
        enabled: true
    },
    email: {
        enabled: false
    },
    emotion: {
        enabled: true,
        noOpinionAnswer: true,
        maxLabelLength: 18
    },
    geoText: {
        enabled: true
    },
    longText: {
        enabled: false
    },
    number: {
        enabled: true,
        restriction: true
    },
    rating: {
        enabled: true,
        noOpinionAnswer: true
    },
    scale: {
        enabled: true,
        noOpinionAnswer: true,
        maxLabelLength: 21,
        limitsCustomization: {
            min: true,
            max: true
        },
        showMiddleLabel: true
    },
    shortText: {
        enabled: false
    },
    sidebyside: {
        enabled: false,
        noOpinionAnswer: true,
        noAnswer: true
    },
    table: {
        enabled: false,
        canUseOptionAttachment: false,
        advancedRowLabelFormat: true,
        advancedColumnLabelFormat: true,
        maxColumnLabelLength: 30,
        maxRowLabelLength: 130,
        maxColumnImages: 1,
        maxRowImages: 1
    },
    tableScale: {
        enabled: false,
        canUseOptionAttachment: false,
        advancedRowLabelFormat: true,
        noOpinionAnswer: true,
        maxColumnLabelLength: 30,
        maxRowLabelLength: 130,
        maxRowImages: 1,
        limitsCustomization: {
            min: true,
            max: true
        }
    },
    markup: {
        enabled: false
    },
    canAddQuestions: true,
    canRemoveQuestions: true,
    canMoveQuestions: false,
    canChangeLogic: true,
    canChangeQuestionType: true
};

export const SCREENING_FEATURES: Features = {
    advancedLogic: false,
    advancedQuestionLabelFormat: false,
    canChangeQuestionLabelAttachmentPosition: false,
    pagesMode: false,
    maxQuestions: 7,
    choice: {
        enabled: true,
        advancedOptionLabelFormat: true,
        canUseOptionAttachment: false,
        noAnswer: true,
        noOpinionAnswer: true,
        optionLogic: true,
        otherAnswer: true,
        otherAnswerWithText: true,
        restriction: true
    },
    canAddQuestions: true,
    canRemoveQuestions: true,
    canMoveQuestions: false,
    canChangeLogic: false,
    canChangeQuestionType: true
};

export const SIMPLE_SURVEY_FEATURES: Features = {
    questionLabelAttachments: {
        image: true,
        video: false
    },
    maxQuestionLabelLength: 68,
    canEditBranding: true,
    brandingEdit: {
        logo: {enabled: true},
        colors: {enabled: true},
        bg: {enabled: true},
        underlay: {enabled: true},
        fonts: {enabled: true},
        footer: {enabled: true},
        progress: {enabled: true}
    },
    canEditTheme: true,
    copy: DEFAULT_COPY_CONFIG,
    choice: {
        enabled: true,
        maxOptionLabelLength: 40,
        optionCountLimit: 6,
        specialOptionCountLimit: 1,
        otherAnswer: true,
        noAnswer: true
    },
    emotion: {
        enabled: true,
        noOpinionAnswer: true,
        maxLabelLength: 18
    },
    rating: {
        enabled: true,
        noOpinionAnswer: true,
        maxValue: 5
    },
    scale: {
        enabled: true,
        noOpinionAnswer: true,
        maxLabelLength: 21,
        limitsCustomization: {
            min: false,
            max: true
        }
    },
    canAddQuestions: true,
    canRemoveQuestions: true,
    canMoveQuestions: true,
    canChangeLogic: true,
    canChangeQuestionType: true
};

export const CREATE_SEGMENT_SURVEY_FEATURES: Features = {
    questionLabelAttachments: {
        image: true,
        video: false
    },
    copy: DEFAULT_COPY_CONFIG,
    maxQuestionLabelLength: 68,
    choice: {
        enabled: true,
        maxOptionLabelLength: 40,
        optionCountLimit: 6,
        specialOptionCountLimit: 1,
        otherAnswer: true,
        noAnswer: true
    },
    canAddQuestions: true,
    canRemoveQuestions: true,
    canMoveQuestions: true,
    canChangeLogic: true,
    canChangeQuestionType: true
};

export const FEATURES_BY_PURPOSE: {[key in SurveyPreset]?: Features} = {
    [SurveyPreset.SimpleDesignEvaluation]: {
        copy: DEFAULT_COPY_CONFIG,
        questionLabelAttachments: {
            image: true,
            video: false
        },
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        },
        scale: {
            enabled: true,
            noOpinionAnswer: true,
            maxLabelLength: 21
        }
    },
    [SurveyPreset.CompetitorAnalysis]: {
        copy: DEFAULT_COPY_CONFIG,
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        }
    },
    [SurveyPreset.TestSlogans]: {
        copy: DEFAULT_COPY_CONFIG,
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        }
    },
    [SurveyPreset.TestVideo]: {
        copy: DEFAULT_COPY_CONFIG,
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        }
    },
    [SurveyPreset.CompareSlogans]: {
        copy: DEFAULT_COPY_CONFIG,
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        }
    },
    [SurveyPreset.DemandAssessment]: {
        copy: DEFAULT_COPY_CONFIG,
        maxQuestionLabelLength: 68,
        choice: {
            enabled: true,
            maxOptionLabelLength: 40,
            optionCountLimit: 6,
            specialOptionCountLimit: 1,
            otherAnswer: true,
            noAnswer: true
        },
        scale: {
            enabled: true,
            noOpinionAnswer: true,
            maxLabelLength: 21
        }
    },
    [SurveyPreset.ConceptTest]: {
        copy: DEFAULT_COPY_CONFIG,
        scale: {
            enabled: true
        },
        markup: {
            enabled: true
        }
    },
    [SurveyPreset.YandexSerpSurvey]: {
        advancedQuestionLabelFormat: true,
        canChangeQuestionLabelAttachmentPosition: true,
        copy: {
            access: {enabled: true}
        },
        pagesMode: false,
        questionLabelAttachments: {
            image: true,
            video: true
        },
        requiredSetting: true,
        choice: {
            enabled: true,
            advancedOptionLabelFormat: false,
            canUseOptionAttachment: false,
            noAnswer: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: false,
            optionLogic: false,
            restriction: false
        },
        emotion: {
            enabled: true,
            noOpinionAnswer: true,
            maxLabelLength: 18
        },
        longText: {
            enabled: true
        },
        canAddQuestions: true,
        canRemoveQuestions: true,
        canMoveQuestions: true,
        canChangeLogic: true,
        canChangeQuestionType: true
    },
    [SurveyPreset.YandexActiveFeedback]: {
        advancedQuestionLabelFormat: true,
        canChangeQuestionLabelAttachmentPosition: true,
        canRotateQuestions: true,
        copy: {
            access: {enabled: true}
        },
        pagesMode: true,
        canEditBranding: true,
        brandingEdit: {
            colors: {enabled: true},
            fonts: {enabled: true},
            progress: {enabled: true},
            json: {enabled: true}
        },
        canEditTheme: true,
        questionLabelAttachments: {
            image: true,
            video: true
        },
        requiredSetting: true,
        choice: {
            enabled: true,
            advancedOptionLabelFormat: true,
            canUseOptionAttachment: true,
            noAnswer: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            optionLogic: true,
            restriction: true
        },
        email: {
            enabled: true,
            hideDescription: true
        },
        emotion: {
            enabled: true,
            noOpinionAnswer: true,
            labelsEnabled: true,
            maxLabelLength: 18,
            otherAnswer: true,
            otherAnswerWithText: true
        },
        longText: {
            enabled: true
        },
        rating: {
            enabled: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            noOpinionAnswer: true
        },
        number: {
            enabled: true,
            restriction: true
        },
        shortText: {
            enabled: true
        },
        scale: {
            enabled: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            canChangeLabelsPosition: true,
            limitsCustomization: {
                min: true,
                max: true
            },
            showMiddleLabel: true
        },
        canAddQuestions: true,
        canRemoveQuestions: true,
        canMoveQuestions: true,
        canChangeLogic: true,
        canChangeQuestionType: true,
        advancedLogic: true,
        markup: {
            enabled: true,
            hasTimeLimit: false
        }
    },
    [SurveyPreset.YandexPassiveFeedback]: {
        advancedQuestionLabelFormat: true,
        canChangeQuestionLabelAttachmentPosition: true,
        copy: {
            access: {enabled: true}
        },
        canRotateQuestions: true,
        pagesMode: true,
        canEditBranding: true,
        brandingEdit: {
            colors: {enabled: true},
            fonts: {enabled: true},
            progress: {enabled: true},
            json: {enabled: true}
        },
        canEditTheme: true,
        questionLabelAttachments: {
            image: true,
            video: true
        },
        requiredSetting: true,
        choice: {
            enabled: true,
            advancedOptionLabelFormat: true,
            canUseOptionAttachment: true,
            noAnswer: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            optionLogic: true,
            restriction: true
        },
        email: {
            enabled: true,
            hideDescription: true
        },
        emotion: {
            enabled: true,
            noOpinionAnswer: true,
            labelsEnabled: true,
            maxLabelLength: 18,
            otherAnswer: true,
            otherAnswerWithText: true
        },
        longText: {
            enabled: true
        },
        rating: {
            enabled: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            noOpinionAnswer: true
        },
        number: {
            enabled: true,
            restriction: true
        },
        shortText: {
            enabled: true
        },
        scale: {
            enabled: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            canChangeLabelsPosition: true,
            limitsCustomization: {
                min: true,
                max: true
            },
            showMiddleLabel: true
        },
        canAddQuestions: true,
        canRemoveQuestions: true,
        canMoveQuestions: true,
        canChangeLogic: true,
        canChangeQuestionType: true,
        advancedLogic: true,
        markup: {
            enabled: true,
            hasTimeLimit: false
        }
    },
    [SurveyPreset.YandexMorda]: {
        advancedQuestionLabelFormat: false,
        canChangeQuestionLabelAttachmentPosition: true,
        copy: {
            access: {enabled: true}
        },
        canRotateQuestions: false,
        canEditBranding: false,
        canEditTheme: true,
        pagesMode: true,
        requiredSetting: true,
        maxQuestionLabelLength: 82,
        choice: {
            enabled: true,
            advancedOptionLabelFormat: false,
            canUseOptionAttachment: false,
            noAnswer: true,
            noOpinionAnswer: true,
            otherAnswer: true,
            otherAnswerWithText: true,
            optionLogic: true,
            restriction: true,
            maxOptionLabelLength: 55
        },
        shortText: {
            enabled: true
        },
        longText: {
            enabled: true
        },
        scale: {
            enabled: true,
            noOpinionAnswer: false,
            otherAnswer: false,
            otherAnswerWithText: false,
            canChangeLabelsPosition: true,
            limitsCustomization: {
                min: true,
                max: true
            },
            showMiddleLabel: false
        },
        canAddQuestions: true,
        canRemoveQuestions: true,
        canMoveQuestions: true,
        canChangeLogic: true,
        canChangeQuestionType: true,
        advancedLogic: true,
        markup: {
            enabled: true,
            hasTimeLimit: false
        }
    }
};
